/* ═══════════════════════════════════════════════════════════════════════════
   KITSTERS - QR TUTORIAL PAGE
   ═══════════════════════════════════════════════════════════════════════════
   Two jobs, both progressive enhancements - with this file blocked the page
   still renders and reads correctly:

   1. Scroll reveal. The hidden-then-fade-in state is gated behind the `kqr--anim`
      class added here, so a no-JS visitor never gets a permanently-invisible
      page.
   2. A tickable step checklist, persisted per page handle in localStorage. The
      QR audience is holding a half-built kit in one hand; being able to tap a
      step off and come back to it later is the whole point.

   Everything is queried by data-attribute and scoped to the section root, so
   splitting this section later cannot silently break the lookups
   (CLAUDE.md §10.35).
   ═══════════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  var STORE_PREFIX = 'kitsters:qr-steps:';

  function prefersReducedMotion() {
    return (
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  /* localStorage is unavailable in some private-browsing modes and throws on
     write when the quota is full - never let that take the checklist down. */
  function readDone(key) {
    try {
      var raw = window.localStorage.getItem(STORE_PREFIX + key);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function writeDone(key, list) {
    try {
      window.localStorage.setItem(STORE_PREFIX + key, JSON.stringify(list));
    } catch (e) {
      /* no-op - ticking still works for this visit, it just won't persist */
    }
  }

  function initReveal(root) {
    var targets = root.querySelectorAll('.kqr__reveal');
    if (!targets.length) return;

    if (!('IntersectionObserver' in window) || prefersReducedMotion()) {
      return; /* leave everything visible - never add the hiding class */
    }

    root.classList.add('kqr--anim');

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );

    Array.prototype.forEach.call(targets, function (el, i) {
      el.style.transitionDelay = Math.min(i, 3) * 80 + 'ms';
      observer.observe(el);
    });
  }

  function initChecklist(root) {
    var list = root.querySelector('[data-kqr-panels]');
    var progress = root.querySelector('[data-kqr-progress]');
    if (!list) return;

    var panels = list.querySelectorAll('[data-kqr-step]');
    if (!panels.length) return;

    var key = root.getAttribute('data-kqr-key') || 'default';
    var total = panels.length;
    var done = readDone(key).filter(function (i) {
      return typeof i === 'number' && i >= 0 && i < total;
    });

    var countEl = progress && progress.querySelector('[data-kqr-progress-count]');
    var fillEl = progress && progress.querySelector('[data-kqr-progress-fill]');
    var doneEl = progress && progress.querySelector('[data-kqr-progress-done]');

    if (progress) progress.hidden = false;

    function render() {
      Array.prototype.forEach.call(panels, function (panel) {
        var idx = parseInt(panel.getAttribute('data-kqr-step'), 10);
        panel.setAttribute('aria-pressed', done.indexOf(idx) > -1 ? 'true' : 'false');
      });

      if (countEl) countEl.textContent = String(done.length);
      if (fillEl) fillEl.style.width = Math.round((done.length / total) * 100) + '%';
      if (doneEl) doneEl.hidden = done.length < total;
    }

    function toggle(idx) {
      var at = done.indexOf(idx);
      if (at > -1) {
        done.splice(at, 1);
      } else {
        done.push(idx);
      }
      writeDone(key, done);
      render();
    }

    Array.prototype.forEach.call(panels, function (panel) {
      panel.addEventListener('click', function () {
        var idx = parseInt(panel.getAttribute('data-kqr-step'), 10);
        if (isNaN(idx)) return;
        toggle(idx);
      });
    });

    render();
  }

  function init() {
    var roots = document.querySelectorAll('[data-kqr-root]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.dataset.kqrReady === '1') return;
      root.dataset.kqrReady = '1';
      initReveal(root);
      initChecklist(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Theme editor: re-init when this section is re-rendered. */
  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector
      ? event.target.querySelector('[data-kqr-root]')
      : null;
    if (root) {
      root.dataset.kqrReady = '';
      init();
    }
  });
})();
